import { useParams } from 'react-router';
import { TagProps, TagValueProps } from 'spindl-shared-components';
import { DatabasePageParams } from '../../components/pages/DatabasePage/types';
import { getTranslation } from '../reactUtils';
import { DropDownOption, MainCellProps, MainModalFieldProps } from '../types';
import { debounce, optionToValue } from '../utils';
import { SingleSelectFieldProps } from './types';
import { useSelectField } from './useSelectField';

export const useSingleSelectField = (mainProps: MainCellProps | MainModalFieldProps): SingleSelectFieldProps => {
  const { dataType, updateCellValue } = mainProps;
  const {
    options,
    selected,
    onDropdownToggle,
    setSelected,
    searchQuery,
    setSearchQuery,
    totalItemsFound,
    itemsDisplayed,
  } = useSelectField<string>(mainProps, '');

  const { databaseSlug, tableSlug } = useParams<DatabasePageParams>();

  const debouncedSetSearchQuery = debounce(setSearchQuery, 500);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateSelectedOption = async (event?: React.MouseEvent<HTMLElement>, metadata?: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    const option = (event?.target as HTMLDivElement).textContent || '';
    setSelected(option);

    const entryValue = optionToValue(option, dataType, metadata);
    if (entryValue !== undefined && tableSlug) {
      await updateCellValue?.(databaseSlug, tableSlug, entryValue);
    }
  };

  const unselectOption = async (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setSelected('');
    if (tableSlug) {
      await updateCellValue?.(databaseSlug, tableSlug, '');
    }
  };

  const handleKeyDown = async (e?: React.KeyboardEvent<HTMLInputElement>) => {
    e?.stopPropagation();
    const firstOption = options?.length ? options[0] : undefined;

    if (firstOption && searchQuery && e?.key === 'Enter') {
      const { text, value: optionValue } = firstOption.metadata as DropDownOption;
      if (text && optionValue && tableSlug) {
        setSelected(text);
        await updateCellValue?.(databaseSlug, tableSlug, optionValue);
      }
    }
  };

  const tags: (TagProps & TagValueProps)[] = !selected ? [] : [{
    text: { value: selected },
    state: 'Selected',
    button: {
      onMouseDown: unselectOption,
    },
  }];

  return {
    cellSelectDropDown: {
      toggleProps: {
        tagList: {
          tags: tags,
        },
      },
      menuProps: {
        onDropdownToggle,
        dropDownItems: options?.map((option) => {
          return {
            ...option,
            onMouseDown: async (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
              await updateSelectedOption(event, option.metadata);
            },
            state: (option.text?.value === selected ? 'Selected' : 'Unselected'),
          };
        }),
        bottomBarText: {
          value: getTranslation('select.items_count', {
            itemsDisplayed,
            totalItemsFound,
          }),
        },
        type: dataType === 'single-reference' ? 'Search' : 'Default',
        searchInput: {
          textValue: searchQuery,
          onTextChanged: (inputValue) => debouncedSetSearchQuery(inputValue),
          onKeyDown: handleKeyDown,
        },
      },
    },
    tag: {
      text: { value: selected },
    },
  };
};
